import { AnimatePresence } from 'framer-motion';
import { CloudSun } from 'lucide-react';
import type { ForecastDaySummary } from './WeatherDashboard';
import { formatForecastDayPill } from '../../lib/forecastWeather';
import type { ForecastSummaryBullets } from '../../lib/weatherForecastSummary';
import { TourStyleCard } from '../ui/TourStyleCard';
import { WeatherConditionsWarning } from './WeatherConditionsWarning';
import { WeatherCodeIcon } from './WeatherCodeIcon';
import { WeatherSubscribeFooter } from './WeatherSubscribeFooter';

interface WeatherSummaryModalProps {
  open: boolean;
  summary: ForecastSummaryBullets | null;
  loading?: boolean;
  days: ForecastDaySummary[];
  districtName?: string | null;
  postcode?: string | null;
  unavailableMessage?: string | null;
  onDismiss: () => void;
  onSubscribe: () => void;
  isSubscribed?: boolean;
}

/** Week-at-a-glance forecast — product-tour card styling. */
export function WeatherSummaryModal({
  open,
  summary,
  loading = false,
  days,
  districtName,
  postcode,
  unavailableMessage = null,
  onDismiss,
  onSubscribe,
  isSubscribed = false,
}: WeatherSummaryModalProps) {
  const locationLine = [districtName, postcode].filter(Boolean).join(' · ');
  const today = days[0] ?? null;

  return (
    <AnimatePresence>
      {open && (
        <TourStyleCard
          headerIcon={<CloudSun size={16} className="shrink-0 text-white" />}
          headerLabel="Weather briefing"
          title="This week at a glance"
          onDismiss={onDismiss}
          dismissLabel="Dismiss week at a glance"
          maxWidthClassName="max-w-md"
          footer={
            unavailableMessage ? undefined : (
              <WeatherSubscribeFooter
                isSubscribed={isSubscribed}
                onSubscribe={onSubscribe}
                dismissLabel="View outlook"
                onDismiss={onDismiss}
              />
            )
          }
        >
          {unavailableMessage ? (
            <WeatherConditionsWarning message={unavailableMessage} />
          ) : (
            <>
          {locationLine && (
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{locationLine}</p>
          )}

          <div className="-mx-1 max-h-[45vh] overflow-y-auto px-1 sm:max-h-[52vh]">
            {loading ? (
              <div className="space-y-2.5 animate-pulse" aria-hidden>
                <span className="block h-10 w-full rounded-xl bg-gray-100 dark:bg-gray-800" />
                <span className="block h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
                <span className="block h-3 w-[85%] rounded bg-gray-200 dark:bg-gray-700" />
                <span className="mt-3 block h-24 w-full rounded-xl bg-gray-100 dark:bg-gray-800" />
              </div>
            ) : summary ? (
              <>
                {today && (
                  <div className="mb-3 flex items-center gap-3 rounded-xl border border-gray-200/80 bg-gray-50 px-3 py-2.5 dark:border-gray-700/60 dark:bg-gray-800/60">
                    <WeatherCodeIcon code={today.weather_code} size={32} />
                    <div className="min-w-0">
                      {today.temperature_c != null && (
                        <p className="text-lg font-semibold tabular-nums leading-none text-gray-900 dark:text-white">
                          {Math.round(today.temperature_c)}°C
                        </p>
                      )}
                      <p className="mt-1 text-xs leading-snug text-gray-600 dark:text-gray-300">
                        {summary.today}
                      </p>
                    </div>
                  </div>
                )}

                <ul className="space-y-2.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  <li>
                    <span className="font-medium text-gray-800 dark:text-gray-200">The week: </span>
                    {summary.week}
                  </li>
                </ul>

                {summary.conditions && (
                  <WeatherConditionsWarning message={summary.conditions} className="mt-3" />
                )}

                {days.length > 0 && (
                  <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full min-w-[280px] border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800/80">
                          <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Day</th>
                          <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Temp</th>
                          <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Rain</th>
                          <th className="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Wind</th>
                        </tr>
                      </thead>
                      <tbody>
                        {days.slice(0, 7).map((day, index) => {
                          const isToday = index === 0;
                          return (
                            <tr
                              key={day.period}
                              className={
                                isToday
                                  ? 'bg-emerald-50/80 dark:bg-emerald-950/25'
                                  : 'border-t border-gray-100 dark:border-gray-800'
                              }
                            >
                              <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                                {formatForecastDayPill(day.period)}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                                {day.temperature_c != null ? `${Math.round(day.temperature_c)}°C` : '—'}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                                {day.precipitation_mm != null ? `${day.precipitation_mm.toFixed(0)} mm` : '—'}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                                {day.wind_speed_ms != null ? `${day.wind_speed_ms.toFixed(1)} m/s` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                Fetching the forecast for your area. This usually takes a moment.
              </p>
            )}
          </div>
            </>
          )}
        </TourStyleCard>
      )}
    </AnimatePresence>
  );
}
